"use client";

import Link from "next/link";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { ACCOUNT_DASHBOARD_URL } from "@/utils/constants";
import useSession from "@/hooks/useSession";

/**
 * Shared final-CTA banner used across the site (footer, blog articles, etc.).
 * Light theme by default so it sits cleanly on white page backgrounds.
 *
 * Props:
 *  - badge:       small eyebrow label above the heading
 *  - title:       main heading
 *  - description: supporting paragraph
 *  - primary:     { label, href }               → solid black button
 *  - secondary:   { label, href }  (optional)   → outlined button
 *  - tertiary:    { label, href }  (optional)   → ghost text link
 *  - withRails:   wrap in the max-width + border-x section shell (footer uses this)
 */
const DEFAULT_TITLE =
  "Ready to move beyond old desktop software and spreadsheets?";
const DEFAULT_DESCRIPTION =
  "Join businesses across India using ABSS to unify CRM, Books, inventory, and GST compliance in one platform. Start free today, no credit card required.";
const DEFAULT_SECONDARY = { label: "Book a Demo", href: "/contact" };

// Shown to signed-in users in place of the acquisition copy above.
const AUTHENTICATED_TITLE = "Pick up right where you left off";
const AUTHENTICATED_DESCRIPTION =
  "Open the account dashboard to jump into any ABSS app your team has access to.";

export default function CtaBanner({
  badge = "Get Started with ABSS",
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  primary = { label: "Start Free Trial", href: "/contact" },
  secondary = DEFAULT_SECONDARY,
  tertiary = { label: "View Pricing", href: "/pricing/books" },
  withRails = false,
}) {
  const { status, user } = useSession();
  const isAuthenticated = status === "authenticated" && user;

  // Only the untouched acquisition copy is swapped - pages that pass their own
  // title/description (blog articles) keep it.
  const resolvedTitle =
    isAuthenticated && title === DEFAULT_TITLE ? AUTHENTICATED_TITLE : title;
  const resolvedDescription =
    isAuthenticated && description === DEFAULT_DESCRIPTION
      ? AUTHENTICATED_DESCRIPTION
      : description;
  // "Book a Demo" is an acquisition CTA - drop it once the user is a customer.
  const resolvedSecondary =
    isAuthenticated && secondary?.label === DEFAULT_SECONDARY.label
      ? null
      : secondary;

  const btnBase =
    "inline-flex h-[46px] w-full items-center justify-center gap-2 rounded-lg px-6 sm:px-8 text-sm geist-semibold transition-all duration-200 sm:w-auto";

  const content = (
    <div className="relative z-10 mx-auto w-full max-w-2xl min-w-0 text-center flex flex-col items-center">
      {badge && (
        <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3.5 py-1.5 text-[12px] geist-semibold text-[#262626] shadow-sm sm:mb-6 sm:px-4">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#DF5D01]" />
          {badge}
        </span>
      )}

      <h2 className="sentient-medium mb-3 w-full px-1 text-[22px] leading-[1.25] tracking-tight text-[#171717] sm:mb-4 sm:text-2xl sm:leading-[1.2] md:text-3xl lg:text-[38px]">
        {resolvedTitle}
      </h2>

      {resolvedDescription && (
        <p className="geist-regular mx-auto mb-6 max-w-lg px-1 text-[13px] leading-relaxed text-[#525252] sm:mb-8 sm:text-sm md:text-base">
          {resolvedDescription}
        </p>
      )}

      <div className="mx-auto flex w-full max-w-[320px] flex-col items-stretch justify-center gap-2.5 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        {isAuthenticated ? (
          <a
            href={ACCOUNT_DASHBOARD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${btnBase} bg-black text-white shadow-md hover:bg-[#171717]`}
          >
            Access Your Apps
            <ArrowUpRight className="h-4 w-4" />
          </a>
        ) : (
          primary && (
            <Link
              href={primary.href}
              className={`${btnBase} bg-black text-white shadow-md hover:bg-[#171717]`}
            >
              {primary.label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          )
        )}
        {resolvedSecondary && (
          <Link
            href={resolvedSecondary.href}
            className={`${btnBase} border border-[#E5E5E5] bg-white text-[#171717] hover:bg-neutral-50 hover:border-[#D4D4D4]`}
          >
            {resolvedSecondary.label}
          </Link>
        )}
        {tertiary && (
          <Link
            href={tertiary.href}
            className={`${btnBase} border border-[#E5E5E5] bg-white text-[#171717] hover:bg-neutral-50 hover:border-[#D4D4D4]`}
          >
            {tertiary.label}
          </Link>
        )}
      </div>
    </div>
  );

  if (withRails) {
    return (
      <section className="w-full border-t border-[#E5E5E5] bg-white overflow-hidden">
        {/* Rail column: left/right borders always visible on the max-width shell */}
        <div className="mx-auto w-full max-w-[1160px] border-x border-[#E5E5E5] box-border">
          <div className="px-5 sm:px-6 md:px-10 lg:px-12 py-12 sm:py-16 md:py-20 lg:py-24">
            {content}
          </div>
        </div>
      </section>
    );
  }

  return content;
}
